import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, Clock, Play, LogOut, Ticket, User, AlertCircle, Film, Users2, Receipt } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { WatchPartyModal, WatchPartyPanel } from '../components/WatchParty';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LiveMyTickets = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [sessionError, setSessionError] = useState(false);
  const [showWatchPartyModal, setShowWatchPartyModal] = useState(false);
  const [selectedEventForParty, setSelectedEventForParty] = useState(null);
  const [activePartyCode, setActivePartyCode] = useState(null);
  const [restrictedEvent, setRestrictedEvent] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('livestreamToken');
    const customerData = localStorage.getItem('livestreamCustomer');
    
    if (!token || !customerData) {
      navigate('/live/login');
      return;
    }

    setCustomer(JSON.parse(customerData));
    
    // Check if coming from a restricted event
    const restrictedEventData = localStorage.getItem('restrictedEvent');
    if (restrictedEventData) {
      try {
        setRestrictedEvent(JSON.parse(restrictedEventData));
      } catch (e) {
        console.error('Error parsing restricted event data:', e);
      }
    }
    
    fetchTickets(token);
  }, [navigate]);

  const fetchTickets = async (token) => {
    try {
      const response = await axios.get(`${API}/livestream/my-tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data.tickets || []);
    } catch (err) {
      if (err.response?.status === 401) {
        // Session expired or logged in elsewhere
        setSessionError(true);
        localStorage.removeItem('livestreamToken');
        localStorage.removeItem('livestreamCustomer');
      }
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('livestreamToken');
    localStorage.removeItem('livestreamCustomer');
    localStorage.removeItem('restrictedEvent');
    localStorage.removeItem('intendedEvent');
    navigate('/live');
  };

  const handleWatch = (eventSlug) => {
    navigate(`/live/watch/${eventSlug}`);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'live':
        return <Badge className="bg-red-500 text-white animate-pulse">🔴 LIVE NOW</Badge>;
      case 'upcoming':
        return <Badge className="bg-blue-500 text-white">Upcoming</Badge>;
      case 'ended':
        return <Badge variant="secondary">Ended</Badge>;
      default:
        return null;
    }
  };

  if (sessionError) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
        <Card className="bg-[#111111] border-[#222222] max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">Session Expired</h2>
            <p className="text-gray-400 mb-6">
              You have been logged out because your account was accessed from another device.
            </p>
            <Button 
              className="w-full bg-red-500 hover:bg-red-600"
              onClick={() => navigate('/live/login')}
            >
              Sign In Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
          <div 
            className={`flex items-center gap-2 sm:gap-3 ${restrictedEvent ? 'cursor-default' : 'cursor-pointer'}`}
            onClick={() => !restrictedEvent && navigate('/live')}
          >
            {restrictedEvent?.custom_logo_url ? (
              <img 
                src={restrictedEvent.custom_logo_url} 
                alt={restrictedEvent.title || 'Event'} 
                className="h-10 max-w-[150px] object-contain"
              />
            ) : (
              <>
                <img 
                  src="/ltv-live-logo.png" 
                  alt="LTV Live" 
                  className="w-8 h-8 sm:w-10 sm:h-10 object-contain brightness-0 invert"
                />
                <span className="text-lg sm:text-xl font-bold">LTV Live</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Only show VOD and Browse Events if NOT in restricted mode */}
            {!restrictedEvent && (
              <>
                <Button 
                  size="sm"
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white border-0 rounded-xl"
                  onClick={() => navigate('/vod')}
                >
                  <Film className="h-4 w-4 mr-1" />
                  VOD
                </Button>
              </>
            )}
            {customer && (
              <Button 
                size="sm"
                className="bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500 text-white border-0 rounded-xl"
                onClick={() => navigate('/live/account')}
              >
                <User className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">{customer.full_name}</span>
              </Button>
            )}
            <Button 
              size="sm"
              className="bg-white/10 hover:bg-white/20 text-white border-0 rounded-xl"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">My Tickets</h1>
            <p className="text-sm sm:text-base text-gray-400">Your purchased live stream access</p>
          </div>
          {/* Only show Browse Events button if NOT in restricted mode */}
          {!restrictedEvent && (
            <Button 
              onClick={() => navigate('/live')}
              className="bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white border-0 rounded-xl w-full sm:w-auto"
            >
              <Ticket className="h-4 w-4 mr-2" />
              Browse Events
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin h-8 w-8 border-2 border-white border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-gray-400">Loading your tickets...</p>
          </div>
        ) : tickets.length === 0 ? (
          <Card className="bg-[#111111] border-[#222222]">
            <CardContent className="p-12 text-center">
              <Ticket className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2 text-white">No Tickets Yet</h3>
              <p className="text-gray-400 mb-6">You haven't purchased any live stream tickets yet.</p>
              <Button 
                onClick={() => navigate('/live')}
                className="bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white border-0 rounded-xl"
              >
                Browse Events
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {tickets.map((ticket) => (
              <Card key={ticket.id} className="bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border-purple-500/30 hover:border-purple-500/50 transition-all duration-300 overflow-hidden">
                <CardContent className="p-6">
                  {/* Event Header with Thumbnail */}
                  <div className="flex flex-col lg:flex-row gap-6">
                    {/* Event Thumbnail */}
                    <div className="relative lg:w-64 h-40 flex-shrink-0 rounded-xl overflow-hidden">
                      {ticket.event?.thumbnail_url ? (
                        <img 
                          src={ticket.event.thumbnail_url} 
                          alt={ticket.event.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-purple-900 to-pink-900 flex items-center justify-center">
                          <Film className="h-12 w-12 text-white/30" />
                        </div>
                      )}
                      {/* Status Badge Overlay */}
                      <div className="absolute top-2 left-2">
                        {ticket.event && getStatusBadge(ticket.event.status)}
                      </div>
                    </div>
                    
                    {/* Event Info */}
                    <div className="flex-1 flex flex-col justify-between">
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">{ticket.event?.title || 'Event'}</h3>
                        
                        {ticket.event?.description && (
                          <p className="text-gray-400 text-sm mb-4 line-clamp-2">{ticket.event.description}</p>
                        )}
                        
                        {ticket.event && (
                          <div className="flex flex-wrap gap-4 text-sm">
                            <div className="flex items-center gap-2 text-purple-400">
                              <Calendar className="h-4 w-4" />
                              <span>{formatDate(ticket.event.event_date)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-pink-400">
                              <Clock className="h-4 w-4" />
                              <span>{formatTime(ticket.event.event_date)}</span>
                            </div>
                          </div>
                        )}

                        <p className="text-xs text-gray-500 mt-3">
                          Purchased: {formatDate(ticket.purchased_at)}
                        </p>

                        {/* VOD Available Notice */}
                        {ticket.vod_video && (
                          <div className="mt-3 flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="vod-available-notice">
                            <Film className="h-4 w-4 text-red-400 flex-shrink-0" />
                            <p className="text-sm text-red-300">Recording available on VOD — <button className="underline hover:text-red-200" onClick={() => navigate(`/vod/video/${ticket.vod_video.slug}`)}>Watch now</button></p>
                          </div>
                        )}
                      </div>

                      {/* Footer with Price and Actions */}
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                        <div className="flex items-center gap-4">
                          <div>
                            <p className="text-2xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                              £{ticket.amount_paid?.toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-500">Paid</p>
                          </div>
                          {/* View Receipt Button */}
                          {ticket.receipt_url && (
                            <Button 
                              variant="outline"
                              size="sm"
                              className="border-green-500/50 text-green-400 hover:bg-green-500/10 rounded-xl"
                              onClick={() => window.open(ticket.receipt_url, '_blank')}
                            >
                              <Receipt className="h-4 w-4 mr-1" />
                              Receipt
                            </Button>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          {/* Watch Party Button */}
                          {(ticket.event?.status === 'live' || ticket.event?.status === 'upcoming') && (
                            <Button 
                              variant="outline"
                              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 rounded-xl"
                              onClick={() => {
                                setSelectedEventForParty({ id: ticket.event.id, title: ticket.event.title });
                                setShowWatchPartyModal(true);
                              }}
                            >
                              <Users2 className="h-4 w-4 mr-2" />
                              Watch Party
                            </Button>
                          )}
                          
                          {ticket.event?.status === 'live' ? (
                            <Button 
                              className="bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500 text-white border-0 rounded-xl animate-pulse"
                              onClick={() => handleWatch(ticket.event.slug)}
                            >
                              <Play className="h-4 w-4 mr-2" />
                              Watch Now
                            </Button>
                          ) : ticket.event?.status === 'upcoming' ? (
                            <Button 
                              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white border-0 rounded-xl"
                              onClick={() => handleWatch(ticket.event.slug)}
                            >
                              View Details
                            </Button>
                          ) : ticket.vod_video ? (
                            <Button 
                              className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white border-0 rounded-xl"
                              onClick={() => navigate(`/vod/video/${ticket.vod_video.slug}`)}
                              data-testid="ticket-watch-vod-btn"
                            >
                              <Film className="h-4 w-4 mr-2" />
                              Watch on VOD
                            </Button>
                          ) : (
                            <Button 
                              className="bg-gray-700 text-gray-400 border-0 rounded-xl"
                              disabled
                            >
                              Event Ended
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {/* Watch Party Modal */}
      {selectedEventForParty && (
        <WatchPartyModal
          eventId={selectedEventForParty.id}
          eventTitle={selectedEventForParty.title}
          isOpen={showWatchPartyModal}
          onClose={() => {
            setShowWatchPartyModal(false);
            setSelectedEventForParty(null);
          }}
          onPartyJoined={(party) => {
            setShowWatchPartyModal(false);
            setActivePartyCode(party.party_code);
          }}
        />
      )}
      
      {/* Active Watch Party Panel */}
      {activePartyCode && (
        <div className="fixed bottom-4 right-4 w-80 z-50">
          <WatchPartyPanel 
            partyCode={activePartyCode}
            onLeave={() => setActivePartyCode(null)}
          />
        </div>
      )}
    </div>
  );
};

export default LiveMyTickets;
